/**
 * PresenceSubview — for a single Resource, shows the comparison between what
 * was PLANNED in the planner (ScheduledItems) and what was actually CLOCKED in
 * gk2control (PresenceRecords) for each day in the selected range.
 *
 * Read-only by design: the user never clocks in/out from here (legally we
 * can't clock in for an employee). The "Sincronizar" button backfills records
 * from gk2control in the chosen range in case the live webhook missed any.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { plannerApi, type PlannerResource, type PlannerScheduledItem, type PresenceRecord } from '../../services/api';
import { LoadingSpinner } from '../common';

const inputClass = 'rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';

function toISODate(d: Date): string {
  // Local YYYY-MM-DD. Using d.toISOString() instead would shift to UTC and
  // could land on the previous day for Spain users near midnight.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function startOfDay(d: Date): Date { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function fmtTime(iso: string): string { return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }); }
function fmtDate(d: Date): string { return d.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short' }); }

interface DayBlock {
  date: Date;
  iso: string;
  scheduled: PlannerScheduledItem[];
  records: PresenceRecord[];
}

type Status =
  | { kind: 'ok'; label: string }
  | { kind: 'warn'; label: string }
  | { kind: 'bad'; label: string }
  | { kind: 'info'; label: string }
  | { kind: 'none' };

const LATE_TOLERANCE_MIN = 5;

function computeStatus(block: DayBlock): Status {
  const hasScheduled = block.scheduled.length > 0;
  const ins = block.records.filter((r) => r.type === 'in').sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const outs = block.records.filter((r) => r.type === 'out').sort((a, b) => +new Date(a.date) - +new Date(b.date));
  const hasIn = ins.length > 0;
  const hasOut = outs.length > 0;
  if (!hasScheduled && !hasIn && !hasOut) return { kind: 'none' };
  if (hasScheduled && !hasIn && !hasOut) return { kind: 'bad', label: 'Ausencia' };
  if (!hasScheduled && (hasIn || hasOut)) return { kind: 'info', label: 'Fichaje sin planificación' };

  // Has scheduled. Compare with the earliest start and latest end among shifts.
  const plannedStart = new Date(Math.min(...block.scheduled.map((s) => +new Date(s.start))));
  const plannedEnd = new Date(Math.max(...block.scheduled.map((s) => +new Date(s.end))));
  const tags: string[] = [];
  if (hasIn) {
    const firstIn = new Date(ins[0].date);
    const delayMin = Math.floor((+firstIn - +plannedStart) / 60000);
    if (delayMin > LATE_TOLERANCE_MIN) tags.push(`retraso ${delayMin} min`);
  }
  if (hasOut) {
    const lastOut = new Date(outs[outs.length - 1].date);
    const earlyMin = Math.floor((+plannedEnd - +lastOut) / 60000);
    if (earlyMin > LATE_TOLERANCE_MIN) tags.push(`salida anticipada ${earlyMin} min`);
  }
  if (!hasOut && hasIn) {
    return { kind: 'warn', label: tags.length ? `Pendiente cerrar salida · ${tags.join(' · ')}` : 'Pendiente cerrar salida' };
  }
  if (tags.length === 0) return { kind: 'ok', label: 'Asistencia' };
  return { kind: 'warn', label: tags.join(' · ') };
}

const STATUS_DOT: Record<Status['kind'], string> = {
  ok: 'bg-green-500', warn: 'bg-amber-500', bad: 'bg-red-500', info: 'bg-gray-400', none: 'bg-gray-200',
};

export default function PresenceSubview({ resource, onClose }: { resource: PlannerResource; onClose: () => void }) {
  const today = startOfDay(new Date());
  const [dateSince, setDateSince] = useState(toISODate(addDays(today, -6)));
  const [dateUntil, setDateUntil] = useState(toISODate(today));
  const [scheduled, setScheduled] = useState<PlannerScheduledItem[]>([]);
  const [records, setRecords] = useState<PresenceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        plannerApi.getScheduled({ from: dateSince, to: dateUntil + 'T23:59:59', resourceId: resource._id }),
        plannerApi.getPresenceRecords({ dateSince, dateUntil: dateUntil + 'T23:59:59', resourceId: resource._id }),
      ]);
      setScheduled(s.data?.data || []);
      setRecords(p.data?.data || []);
    } catch {
      toast.error('Error al cargar el control de presencia');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dateSince, dateUntil, resource._id]);

  const sync = async () => {
    if (!resource.employeeNif) { toast.error('Este recurso no tiene NIF de empleado'); return; }
    setSyncing(true);
    try {
      const r = await plannerApi.syncPresence({ dateSince, dateUntil, nif: resource.employeeNif });
      const { inserted = 0, skipped = 0, invalid = 0, reconciled = 0 } = r.data || {};
      toast.success(`Sincronizado: ${inserted} nuevos, ${skipped} repetidos, ${invalid} inválidos, ${reconciled} reasignados`);
      await load();
    } catch (e: unknown) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const days = useMemo<DayBlock[]>(() => {
    const start = new Date(dateSince);
    const end = new Date(dateUntil);
    const out: DayBlock[] = [];
    for (let d = startOfDay(start); +d <= +startOfDay(end); d = addDays(d, 1)) {
      const iso = toISODate(d);
      const dayStart = +d, dayEnd = +addDays(d, 1);
      out.push({
        date: new Date(d),
        iso,
        scheduled: scheduled.filter((s) => { const t = +new Date(s.start); return t >= dayStart && t < dayEnd; }),
        records: records.filter((r) => { const t = +new Date(r.date); return t >= dayStart && t < dayEnd; }),
      });
    }
    return out;
  }, [dateSince, dateUntil, scheduled, records]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Control de presencia</h2>
            <p className="text-sm text-gray-500">
              {resource.name}{resource.employeeNif ? ` · NIF ${resource.employeeNif}` : ' · sin NIF asignado'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-end gap-3 p-4 border-b border-gray-100 bg-gray-50">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Desde</label>
            <input type="date" className={inputClass} value={dateSince} onChange={(e) => setDateSince(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Hasta</label>
            <input type="date" className={inputClass} value={dateUntil} onChange={(e) => setDateUntil(e.target.value)} />
          </div>
          <button onClick={sync} disabled={syncing || !resource.employeeNif}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50"
            title={resource.employeeNif ? 'Trae los fichajes del rango desde gk2control' : 'Asigna primero un NIF al recurso'}>
            {syncing && <LoadingSpinner size="sm" />} Sincronizar
          </button>

          {/* Legend */}
          <div className="ml-auto flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> OK</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Aviso</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Ausencia</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> Sin plan</span>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400"><LoadingSpinner size="lg" /></div>
          ) : days.length === 0 ? (
            <p className="text-sm text-gray-500">Selecciona un rango.</p>
          ) : days.map((b) => {
            const status = computeStatus(b);
            const dot = STATUS_DOT[status.kind];
            return (
              <div key={b.iso} className="border border-gray-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
                    <span className="text-sm font-medium text-gray-900">{fmtDate(b.date)}</span>
                  </div>
                  <span className="text-xs text-gray-500">{status.kind !== 'none' ? status.label : 'Sin actividad'}</span>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-gray-400 mb-1 uppercase tracking-wide">Planificado</p>
                    {b.scheduled.length === 0
                      ? <p className="text-gray-400">—</p>
                      : b.scheduled.map((s) => (
                          <p key={s._id} className="text-gray-700">{fmtTime(s.start)} – {fmtTime(s.end)}{s.clientLabel ? ` · ${s.clientLabel}` : ''}</p>
                        ))}
                  </div>
                  <div>
                    <p className="text-gray-400 mb-1 uppercase tracking-wide">Fichajes</p>
                    {b.records.length === 0
                      ? <p className="text-gray-400">—</p>
                      : b.records
                          .slice()
                          .sort((a, c) => +new Date(a.date) - +new Date(c.date))
                          .map((r) => (
                            <p key={r._id} className="text-gray-700">
                              {r.type === 'in' ? '⏵ Entrada' : '⏸ Salida'} {fmtTime(r.date)}
                              {r.source === 'auto' && <span className="ml-1 text-[10px] text-gray-400">(auto)</span>}
                              {r.source === 'manual' && <span className="ml-1 text-[10px] text-gray-400">(manual)</span>}
                            </p>
                          ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
